import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { useCreateManualServiceMutation } from '../../../store/api/manualServicesApi';

export default function AddManualServicePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [createManualService, { isLoading: isCreating }] = useCreateManualServiceMutation();

  // Form states
  const [key, setKey] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [titleEn, setTitleEn] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [descriptionEn, setDescriptionEn] = useState<string>('');
  const [visible, setVisible] = useState<boolean>(true);
  const [maintenance, setMaintenance] = useState<boolean>(false);
  
  // Validation constants
  const MAX_DESCRIPTION_LENGTH = 4096;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!key.trim()) {
      toast.error(t('admin.manualServices.add.error.keyRequired') || 'Key is required');
      return;
    }

    // Validate key: only English letters, numbers, and underscore
    const keyPattern = /^[a-zA-Z]+$/;
    if (!keyPattern.test(key.trim())) {
      toast.error(t('admin.manualServices.add.error.keyInvalid') || 'Key must contain only English letters, numbers, and underscore');
      return;
    }

    if (!title.trim()) {
      toast.error(t('admin.manualServices.add.error.titleRequired') || 'Title is required');
      return;
    }

    // Validate description length
    if (description.trim().length > MAX_DESCRIPTION_LENGTH) {
      toast.error(
        t('admin.manualServices.add.error.descriptionTooLong') ||
          `Description must not exceed ${MAX_DESCRIPTION_LENGTH} characters`
      );
      return;
    }

    // Validate descriptionEn length
    if (descriptionEn.trim().length > MAX_DESCRIPTION_LENGTH) {
      toast.error(
        t('admin.manualServices.add.error.descriptionEnTooLong') ||
          `English description must not exceed ${MAX_DESCRIPTION_LENGTH} characters`
      );
      return;
    }

    try {
      await createManualService({
        key: key.trim(),
        title: title.trim(),
        titleEn: titleEn.trim() || undefined,
        description: description.trim() || undefined,
        descriptionEn: descriptionEn.trim() || undefined,
        visible,
        maintenance,
      }).unwrap();

      toast.success(t('admin.manualServices.add.success') || 'Manual service created successfully');
      navigate(`/admin/manual-services/${key.trim()}`, { replace: true });
    } catch (err) {
      const error = err as { data?: { error?: string }; message?: string };
      toast.error(error?.data?.error || error?.message || 'Failed to create manual service');
    }
  };

  return (
    <div className="px-2 sm:px-4 lg:px-0">
      {/* Header */}
      <div
        className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between"
        style={{
          animation: 'fadeIn 0.5s ease-out',
        }}
      >
        <div>
          <Link
            to="/admin/manual-services"
            className="text-brand-accent hover:text-brand-accent/80 font-semibold mb-4 inline-flex items-center gap-2 transition-all duration-200 hover:underline"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {t('admin.manualServices.back') || 'Back to Services'}
          </Link>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-brand-accent">
            {t('admin.manualServices.add.title') || 'Add New Manual Service'}
          </h1>
          <p className="text-neutral-medium text-sm sm:text-base mt-2">
            {t('admin.manualServices.add.subtitle') || 'Create a new manual service that will appear in the bot menu'}
          </p>
        </div>
      </div>

      {/* Form */}
      <div
        className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50"
        style={{
          animation: 'slideUp 0.5s ease-out 0.1s both',
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit(e);
          }}
          className="space-y-6"
        >
          {/* Key */}
          <div>
            <label className="block text-sm font-medium text-neutral-light mb-2">
              {t('admin.manualServices.form.key') || 'Key'} <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={key}
              onChange={(e) => {
                // Only allow English letters, numbers, and underscore
                const value = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
                setKey(value);
              }}
              placeholder={t('admin.manualServices.form.keyPlaceholder') || 'e.g., custom_service_1'}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent"
              required
            />
            <p className="text-xs text-neutral-medium mt-1">
              {t('admin.manualServices.form.keyHint') || 'Only English letters, numbers, and underscore are allowed'}
            </p>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-neutral-light mb-2">
              {t('admin.manualServices.form.title') || 'Title (Arabic/Default)'} <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('admin.manualServices.form.titlePlaceholder') || 'Service title in Arabic'}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent"
              required
            />
          </div>

          {/* Title En */}
          <div>
            <label className="block text-sm font-medium text-neutral-light mb-2">
              {t('admin.manualServices.form.titleEn') || 'Title (English)'}
            </label>
            <input
              type="text"
              value={titleEn}
              onChange={(e) => setTitleEn(e.target.value)}
              placeholder={t('admin.manualServices.form.titleEnPlaceholder') || 'Service title in English (optional)'}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent"
            />
            <p className="text-xs text-neutral-medium mt-1">
              {t('admin.manualServices.form.titleEnHint') ||
                'If user language is English and this is set, it will be displayed instead of title'}
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-neutral-light mb-2">
              {t('admin.manualServices.form.description') || 'Description (Arabic)'}
            </label>
            <textarea
              value={description}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= MAX_DESCRIPTION_LENGTH) {
                  setDescription(value);
                }
              }}
              placeholder={t('admin.manualServices.form.descriptionPlaceholder') || 'Service description in Arabic (optional)'}
              rows={4}
              maxLength={MAX_DESCRIPTION_LENGTH}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent resize-none"
            />
            <p className="text-xs text-neutral-medium mt-1">
              {description.length}/{MAX_DESCRIPTION_LENGTH} {t('common.characters') || 'characters'}
              {description.length > MAX_DESCRIPTION_LENGTH * 0.9 && (
                <span className="text-yellow-400 ml-1">
                  ({t('admin.manualServices.form.nearLimit') || 'Near limit'})
                </span>
              )}
            </p>
          </div>

          {/* Description En */}
          <div>
            <label className="block text-sm font-medium text-neutral-light mb-2">
              {t('admin.manualServices.form.descriptionEn') || 'Description (English)'}
            </label>
            <textarea
              value={descriptionEn}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= MAX_DESCRIPTION_LENGTH) {
                  setDescriptionEn(value);
                }
              }}
              placeholder={t('admin.manualServices.form.descriptionEnPlaceholder') || 'Service description in English (optional)'}
              rows={4}
              maxLength={MAX_DESCRIPTION_LENGTH}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent resize-none"
            />
            <p className="text-xs text-neutral-medium mt-1">
              {descriptionEn.length}/{MAX_DESCRIPTION_LENGTH} {t('common.characters') || 'characters'}
              {descriptionEn.length > MAX_DESCRIPTION_LENGTH * 0.9 && (
                <span className="text-yellow-400 ml-1">
                  ({t('admin.manualServices.form.nearLimit') || 'Near limit'})
                </span>
              )}
            </p>
            <p className="text-xs text-neutral-medium mt-1">
              {t('admin.manualServices.form.descriptionEnHint') ||
                'If user language is English, this description will be displayed if available, otherwise Arabic description will be used as fallback'}
            </p>
          </div>

          {/* Visible & Maintenance */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={visible}
                  onChange={(e) => setVisible(e.target.checked)}
                  className="w-5 h-5 rounded border-neutral-medium bg-neutral-dark text-brand-accent focus:ring-brand-accent"
                />
                <span className="text-sm font-medium text-neutral-light">
                  {t('admin.manualServices.form.visible') || 'Visible'}
                </span>
              </label>
              <p className="text-xs text-neutral-medium mt-1">
                {t('admin.manualServices.form.visibleHint') || 'Only visible services are shown to users'}
              </p>
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={maintenance}
                  onChange={(e) => setMaintenance(e.target.checked)}
                  className="w-5 h-5 rounded border-neutral-medium bg-neutral-dark text-brand-accent focus:ring-brand-accent"
                />
                <span className="text-sm font-medium text-neutral-light">
                  {t('admin.manualServices.form.maintenance') || 'Maintenance Mode'}
                </span>
              </label>
              <p className="text-xs text-neutral-medium mt-1">
                {t('admin.manualServices.form.maintenanceHint') ||
                  'Services in maintenance mode are not shown to users'}
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-4 pt-4">
            <button
              type="submit"
              disabled={isCreating}
              className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating
                ? t('admin.manualServices.add.creating') || 'Creating...'
                : t('admin.manualServices.add.create') || 'Create Service'}
            </button>
            <Link
              to="/admin/manual-services"
              className="px-6 py-3 bg-neutral-medium text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-medium/80 transition-all duration-200 hover:scale-105 active:scale-95"
            >
              {t('common.cancel') || 'Cancel'}
            </Link>
          </div>
        </form>
      </div>

      {isCreating && <LoadingSpinner />}
    </div>
  );
}



